import { useEffect, useState } from "react";
import SortableTable from "../../components/SortableTable";
import NoRecordsAvailable from "../../components/NoRecordsAvailable";
import {
  API_GET_ACCESS_TYPES,
  API_APPROVE_ACCESS_TYPE_REQUEST,
} from "../../config/Endpoints";
import axios from "axios";
import Cookies from "js-cookie";
import { formatDate } from "../../utils/helpers";
import * as Yup from "yup";
import validations from "../../config/validation";
import { toast } from "react-toastify";
import { TailSpin, ThreeDots } from "react-loader-spinner";
import Badge from "../../components/Badge";

const ApproveAccessTypeRequests = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState();
  const accessToken = Cookies.get("accessToken");

  const columns = [
    { key: "accessType", label: "Access Type" },

    { key: "requestedDate", label: "Requested Date" },
    { key: "comment", label: "Description" },
    {
      key: "status",
      label: "Status",
      render: (item) => (
        <Badge
          type={
            item.status === "PENDING"
              ? "warning"
              : item.status === "REJECTED"
              ? "danger"
              : "success"
          }
          className="text-capitalize"
        >
          {item.status === "PENDING"
            ? "Pending"
            : item.status === "REJECTED"
            ? "Rejected"
            : "Approved"}
        </Badge>
      ),
    },

    {
      key: "action",
      label: "Action",
      render: (item) => (
        <a
          data-bs-toggle="modal"
          data-bs-target="#staticBackdropAccessTypeRequestAction"
          onClick={() => setSelectedRequest(item._original)}
        >
          View
        </a>
      ),
      sortable: false,
    },
  ];

  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    setTableData(
      requests?.map((item) => ({
        accessType: item.name,
        requestedDate: formatDate(item.createdAt),
        comment: decodeURIComponent(item.description || ""),
        status: item.status || "-",
        _original: item,
      })) || []
    );
  }, [requests]);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_GET_ACCESS_TYPES}?status=PENDING`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const accessRequests = res?.data?.data?.accessTypes || [];

      setRequests(accessRequests);
    } catch (err) {
      toast.error("Failed to fetch access type requests.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  return (
    <div className="p-2">
      {isLoading && (
        <div className="overlay">
          <div className="mt-5">
            <TailSpin height="100" width="100" color="blue" />
          </div>
        </div>
      )}

      <div
        className="modal fade"
        id="staticBackdropAccessTypeRequestAction"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex="-1"
        aria-labelledby="staticBackdropLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <AccessTypeRequestActionModal
              selectedRequest={selectedRequest}
              setRequests={setRequests}
              fetchRequests={fetchRequests}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          {!isLoading && (
            <>
              {tableData.length > 0 ? (
                <SortableTable columns={columns} data={tableData} />
              ) : (
                <div className="container">
                  <NoRecordsAvailable />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AccessTypeRequestActionModal = ({
  selectedRequest: request,
  setRequests,
  fetchRequests,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ status: "", comment: "" });
  const [errors, setErrors] = useState({});
  const accessToken = Cookies.get("accessToken");

  const validationSchema = Yup.object().shape({
    status: Yup.string().required("Action is required."),

    comment: Yup.string()
      .transform((value) => value.trim())
      .when("status", {
        is: "REJECTED",
        then: (schema) =>
          schema
            .required("Reason is required.")
            .min(5, "Reason must be at least 5 characters long."),
        otherwise: (schema) =>
          schema
            .required("Description is required.")
            .min(5, "Description must be at least 5 characters long."),
      }),
  });
  const columns = [
    { key: "accessType", label: "Access Type" },

    { key: "requestedDate", label: "Requested Date" },
    { key: "comment", label: "Description" },
    {
      key: "status",
      label: "Status",
      render: (item) => (
        <Badge
          type={
            item.status === "PENDING"
              ? "warning"
              : item.status === "REJECTED"
              ? "danger"
              : "success"
          }
          className="text-capitalize"
        >
          {item.status === "PENDING"
            ? "Pending"
            : item.status === "REJECTED"
            ? "Rejected"
            : "Approved"}
        </Badge>
      ),
    },
  ];

  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    if (request) {
      setTableData([
        {
          accessType: request.name,

          requestedDate: formatDate(request.createdAt),
          comment: decodeURIComponent(request.description || ""),
          status: request.status || "-",
        },
      ]);
    }
  }, [request]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await validationSchema.validate(formData, { abortEarly: false });

      const res = await axios.put(
        `${API_APPROVE_ACCESS_TYPE_REQUEST}/${request.accessId}?status=${formData.status}`,
        { comment: formData.comment },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      await fetchRequests();

      toast.success(res.data.message || "Request updated successfully.");

      document.querySelector(".cancel-modal").click();
      handleReset();
    } catch (error) {
      if (error.name === "ValidationError") {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else if (error.response && error.response.data) {
        const backendData = error.response.data;

        if (backendData.errors) {
          const fieldErrors = {};
          Object.keys(backendData.errors).forEach((key) => {
            fieldErrors[key] = backendData.errors[key];
            toast.error(`${key}: ${backendData.errors[key]}`);
          });
          setErrors(fieldErrors);
        }

        if (backendData.message) {
          toast.error(backendData.message);
        }
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });

    if (name === "status") {
      setErrors((prev) => ({ ...prev, status: "", comment: "" }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleReset = () => {
    setFormData({ status: "", comment: "" });
    setErrors({});
  };

  return (
    <form onSubmit={handleSubmit}>
      {isLoading && (
        <div className="overlay">
          <TailSpin height="100" width="100" color="blue" />
        </div>
      )}
      <div className="modal-header">
        <h5 className="modal-title">Access Type Request Action</h5>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="modal"
          onClick={handleReset}
        ></button>
      </div>
      <div className="modal-body">
        <div className="px-2 py-4">
          <div className="d-grid inputs gap-4">
            <SortableTable columns={columns} data={tableData} />

            {/* Action Select */}
            <div className="input-group">
              <div className="row">
                <div className="col-md-2"></div>
                <div className="col-md-4 pt-1">
                  <label htmlFor="status" className="has-asterisk">
                    Action
                  </label>
                </div>
                <div className="col-md-4">
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="border"
                    required
                  >
                    <option value="" disabled>
                      Select Action
                    </option>
                    <option value="APPROVED">Approve</option>
                    <option value="REJECTED">Reject</option>
                  </select>
                  {errors.status && (
                    <small className="text-danger">{errors.status}</small>
                  )}

                  {errors.status && (
                    <small className="text-danger">{errors.status}</small>
                  )}
                </div>
                <div className="col-md-2"></div>
              </div>
            </div>

            {/* Comment Textarea */}
            <div className="input-group">
              <div className="row">
                <div className="col-md-2"></div>
                <div className="col-md-4 pt-1">
                  <label htmlFor="comment" className="has-asterisk">
                    {formData.status === "REJECTED" ? "Reason" : "Description"}
                  </label>
                </div>
                <div className="col-md-4">
                  <textarea
                    id="comment"
                    name="comment"
                    rows={3}
                    className="border"
                    value={formData.comment}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                  {errors.comment && (
                    <small className="text-danger">{errors.comment}</small>
                  )}
                </div>
                <div className="col-md-2"></div>
              </div>
            </div>
          </div>

          <div className="action text-center mt-5"></div>
        </div>
      </div>

      <div className="modal-footer justify-content-center">
        <button
          className={`theme-button ${isLoading ? "disabled" : ""}`}
          type="submit"
        >
          {isLoading ? (
            <ThreeDots height="22" width="60" color="white" />
          ) : (
            "Submit"
          )}
        </button>
        <button
          type="button"
          className="theme-button bg-grey mx-3 w-35 cancel-modal"
          data-bs-dismiss="modal"
          onClick={handleReset}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ApproveAccessTypeRequests;
